/**
 * @module UseCanvasState
 * @purpose Coordinator hook for the analysis canvas — owns UI state and delegates data fetching.
 * @depends useAnalysisArtefacts, useAnalysisFacets, types/analysis-artefacts
 * @dependedOnBy CanvasView
 *
 * @rules
 * - Performance: debounces search 250 ms before forwarding to data hooks
 * - State: local useState for UI state; Sets for bookmark/star (in-memory only, lost on refetch)
 * - Errors: propagates error strings from child hooks
 */

import { useState, useCallback, useEffect, useRef } from 'react'
import {
  useAnalysisArtefacts,
  useAnalysisFacets,
} from '../../../hooks/use-analysis-artefacts'
import type { AnalysisArtefact, AnalysisFacets, AnalysisFilters } from '../../../types/analysis-artefacts'
import type { ClusterDetail, ResolvedObservation } from '../../../types/reasoning'
import { causalTier } from '../../../lib/assertion-display'
import type { CanvasViewMode, DecoratedArtefact } from './types'

const DEFAULT_VIEW: CanvasViewMode = 'table'
const SEARCH_DEBOUNCE_MS = 250

function decorateItems(
  items: AnalysisArtefact[],
  bookmarkedIds: Set<string>,
  starredIds: Set<string>,
): DecoratedArtefact[] {
  return items.map((item) => ({
    ...item,
    isBookmarked: bookmarkedIds.has(item.artefactId),
    isStarred: starredIds.has(item.artefactId),
  }))
}

function toggleSetMember(prev: Set<string>, id: string): Set<string> {
  const next = new Set(prev)
  if (next.has(id)) {
    next.delete(id)
  } else {
    next.add(id)
  }
  return next
}

export interface UseCanvasStateParams {
  caseId: string | null
}

export interface UseCanvasStateResult {
  items: DecoratedArtefact[]
  nextCursor: string | null
  loading: boolean
  loadingMore: boolean
  error: string | null
  facets: AnalysisFacets | null
  facetsLoading: boolean
  facetsError: string | null
  selectedFilters: AnalysisFilters
  searchQuery: string
  activeView: CanvasViewMode
  selectedArtefactId: string | null
  isExpanded: boolean
  reasoningPanelOpen: boolean
  refetch: () => Promise<void>
  loadMore: () => Promise<void>
  setSearchQuery: (query: string) => void
  setSelectedFilters: (filters: AnalysisFilters) => void
  clearFilters: () => void
  setActiveView: (view: CanvasViewMode) => void
  setSelectedArtefactId: (id: string | null) => void
  toggleDock: () => void
  toggleBookmark: (artefactId: string) => void
  toggleStar: (artefactId: string) => void
  onTagAction: (artefactId: string) => void
  setReasoningPanelOpen: (open: boolean) => void
  drillToSingleArtefact: (artefact: ResolvedObservation) => void
  drillToCluster: (cluster: ClusterDetail, resolved: Map<string, ResolvedObservation>) => void
}

function uniqueEndpointIds(resolvedMap: Map<string, ResolvedObservation>): string[] {
  const seen = new Set<string>()
  for (const obs of resolvedMap.values()) {
    if (obs.endpointId !== undefined) seen.add(obs.endpointId)
  }
  return Array.from(seen)
}

function pickFirstByCausalChain(
  resolvedMap: Map<string, ResolvedObservation>,
): ResolvedObservation | null {
  const observations = Array.from(resolvedMap.values()).filter(
    (obs) => obs.found && obs.sourceArtefactRow !== undefined,
  )
  if (observations.length === 0) return null

  observations.sort((lhs, rhs) => {
    const tierDiff = causalTier(lhs) - causalTier(rhs)
    if (tierDiff !== 0) return tierDiff
    const typeLeft = lhs.sourceArtefactType ?? ''
    const typeRight = rhs.sourceArtefactType ?? ''
    if (typeLeft !== typeRight) return typeLeft.localeCompare(typeRight)
    const idLeft = lhs.sourceArtefactId ?? ''
    const idRight = rhs.sourceArtefactId ?? ''
    return idLeft.localeCompare(idRight)
  })

  return observations[0] ?? null
}

function useDebouncedSearch(query: string): string {
  const [debounced, setDebounced] = useState(query)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (timerRef.current !== null) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => { setDebounced(query) }, SEARCH_DEBOUNCE_MS)
    return () => { if (timerRef.current !== null) clearTimeout(timerRef.current) }
  }, [query])

  return debounced
}

function useSelectionToggles(): {
  bookmarkedIds: Set<string>
  starredIds: Set<string>
  toggleBookmark: (id: string) => void
  toggleStar: (id: string) => void
} {
  const [bookmarkedIds, setBookmarkedIds] = useState<Set<string>>(new Set())
  const [starredIds, setStarredIds] = useState<Set<string>>(new Set())
  const toggleBookmark = useCallback((id: string) => {
    setBookmarkedIds((prev) => toggleSetMember(prev, id))
  }, [])
  const toggleStar = useCallback((id: string) => {
    setStarredIds((prev) => toggleSetMember(prev, id))
  }, [])
  return { bookmarkedIds, starredIds, toggleBookmark, toggleStar }
}

/**
 * Coordinator hook for the analysis canvas.
 *
 * @behaviour Owns local UI state (view mode, selection, dock, search text).
 *            Delegates data fetching to useAnalysisArtefacts and useAnalysisFacets.
 *            Debounces search input by 250 ms before passing to data hooks.
 *            Tracks bookmark/star state in-memory via Sets; lost on refetch (by design).
 * @errors Surfaces error strings from the underlying data hooks.
 */
export function useCanvasState({ caseId }: UseCanvasStateParams): UseCanvasStateResult {
  const [searchQuery, setSearchQueryState] = useState('')
  const [selectedFilters, setSelectedFiltersState] = useState<AnalysisFilters>({})
  const [activeView, setActiveViewState] = useState<CanvasViewMode>(DEFAULT_VIEW)
  const [selectedArtefactId, setSelectedArtefactIdState] = useState<string | null>(null)
  const [isExpanded, setIsExpanded] = useState(false)
  const [reasoningPanelOpen, setReasoningPanelOpenState] = useState(false)

  const debouncedSearch = useDebouncedSearch(searchQuery)
  const { bookmarkedIds, starredIds, toggleBookmark, toggleStar } = useSelectionToggles()

  const artefactsResult = useAnalysisArtefacts({ caseId, search: debouncedSearch, filters: selectedFilters })
  const facetsResult = useAnalysisFacets({ caseId, search: debouncedSearch, filters: selectedFilters })

  const setSearchQuery = useCallback((query: string) => { setSearchQueryState(query) }, [])
  const setSelectedFilters = useCallback((filters: AnalysisFilters) => { setSelectedFiltersState(filters) }, [])
  const clearFilters = useCallback(() => { setSelectedFiltersState({}) }, [])
  const setActiveView = useCallback((view: CanvasViewMode) => { setActiveViewState(view) }, [])
  const setSelectedArtefactId = useCallback((id: string | null) => { setSelectedArtefactIdState(id) }, [])
  const toggleDock = useCallback(() => { setIsExpanded((prev) => !prev) }, [])
  const onTagAction = useCallback((_artefactId: string) => {}, [])
  const setReasoningPanelOpen = useCallback((open: boolean) => { setReasoningPanelOpenState(open) }, [])

  const drillToSingleArtefact = useCallback((artefact: ResolvedObservation) => {
    setReasoningPanelOpenState(false)
    const endpointId = artefact.endpointId
    if (endpointId !== undefined) {
      setSelectedFiltersState((prev) => ({ ...prev, endpointId: [endpointId] }))
    }
    const displayName = artefact.sourceArtefactRow?.displayName ?? ''
    setSearchQueryState(displayName)
    const artefactId = artefact.sourceArtefactRow?.artefactId ?? null
    setSelectedArtefactIdState(artefactId)
  }, [])

  const drillToCluster = useCallback(
    (_cluster: ClusterDetail, resolved: Map<string, ResolvedObservation>) => {
      setReasoningPanelOpenState(false)
      const endpoints = uniqueEndpointIds(resolved)
      setSelectedFiltersState((prev) => ({ ...prev, endpointId: endpoints }))
      setSearchQueryState('')
      const first = pickFirstByCausalChain(resolved)
      setSelectedArtefactIdState(first?.sourceArtefactRow?.artefactId ?? null)
    },
    [],
  )

  return {
    items: decorateItems(artefactsResult.items, bookmarkedIds, starredIds),
    nextCursor: artefactsResult.nextCursor,
    loading: artefactsResult.loading,
    loadingMore: artefactsResult.loadingMore,
    error: artefactsResult.error,
    facets: facetsResult.facets,
    facetsLoading: facetsResult.loading,
    facetsError: facetsResult.error,
    selectedFilters,
    searchQuery,
    activeView,
    selectedArtefactId,
    isExpanded,
    reasoningPanelOpen,
    refetch: artefactsResult.refetch,
    loadMore: artefactsResult.loadMore,
    setSearchQuery,
    setSelectedFilters,
    clearFilters,
    setActiveView,
    setSelectedArtefactId,
    toggleDock,
    toggleBookmark,
    toggleStar,
    onTagAction,
    setReasoningPanelOpen,
    drillToSingleArtefact,
    drillToCluster,
  }
}
